import { createEnv } from '@t3-oss/env-core';
import { z } from 'zod';
import { ALL_LANGUAGES, isLanguageCode, type LanguageCode } from '@s2w/types';

const supportedLanguagesSchema = z
  .string()
  .default(ALL_LANGUAGES.join(','))
  .transform((raw, ctx): readonly LanguageCode[] => {
    const parts = raw
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter((part) => part.length > 0);
    if (parts.length === 0) {
      ctx.addIssue({ code: 'custom', message: 'VITE_SUPPORTED_LANGUAGES must list >= 1 language' });
      return z.NEVER;
    }
    const codes: LanguageCode[] = [];
    for (const part of parts) {
      if (!isLanguageCode(part)) {
        ctx.addIssue({
          code: 'custom',
          message: `Unsupported language code: ${part}. Allowed: ${ALL_LANGUAGES.join(', ')}`,
        });
        return z.NEVER;
      }
      if (!codes.includes(part)) codes.push(part);
    }
    return codes;
  });

const parentOriginSchema = z
  .string()
  .refine(
    (v) => v === '*' || new URL(v).origin === v,
    'Must be a valid origin (scheme + host + optional port, no path) or *',
  );

export const env = createEnv({
  clientPrefix: 'VITE_',
  client: {
    VITE_API_BASE_URL: z.url().default('http://localhost:3001'),
    VITE_SENTRY_DSN: z.url().optional(),
    VITE_SENTRY_ENVIRONMENT: z.string().optional(),
    VITE_SENTRY_RELEASE: z.string().optional(),
    VITE_PARENT_ORIGIN: z.preprocess((raw) => {
      if (raw !== undefined && raw !== null && raw !== '') return raw;
      if (import.meta.env.DEV) {
        if (typeof globalThis !== 'undefined' && 'location' in globalThis) {
          const origin = (globalThis as { location?: { origin?: string } }).location?.origin;
          if (origin) return origin;
        }
        return 'http://localhost:5173';
      }
      return raw;
    }, parentOriginSchema),
    VITE_SUPPORTED_LANGUAGES: supportedLanguagesSchema,
    VITE_SKIN: z.enum(['czr', 'wh']).default('czr'),
    VITE_BRAND_ASSETS_BASE_URL: z.url().optional(),
    // Where "View All NBA Games" sends the Player: the feed's sport, competition
    // name and competition id, which the sportsbook routes with its own
    // navigation (Q16). Per deployment, defaulting to the NBA.
    VITE_NAVIGATE_SPORT: z.string().default('basketball'),
    VITE_NAVIGATE_COMPETITION_NAME: z.string().default('NBA'),
    VITE_NAVIGATE_COMPETITION_ID: z.string().default('5806c896-4eec-4de1-874f-afed93114b8c'),
  },
  runtimeEnv: import.meta.env,
  emptyStringAsUndefined: true,
});
